import { parseFrontmatter } from "@vortex-os/core";
import type { LintFinding, LintRule } from "../types.js";

/**
 * Rule for `data/_memory/*.md` entries: the memory `type` must be a TOP-LEVEL
 * frontmatter field — the index generator and the recall layer read
 * `frontmatter.type` — never nested under `metadata`, and never the generic
 * `note` default (which silently misclassifies the memory in type-aware
 * recall). Scoped to files under a `_memory/` directory; the generated indexes
 * (`_INDEX.md`, `MEMORY.md`) and `_TEMPLATE*` skeletons are skipped, and files
 * in any other directory pass untouched.
 *
 * It does NOT enforce a closed type vocabulary: instances may extend the set
 * (e.g. `infra`, `work`), and the built-in `memory-system` accepts any string.
 * The two things this guards are the observed drift modes — a `metadata.type`
 * the code never reads, and the `type: note` leak from the harness-memory
 * format — both of which `require-frontmatter`/`privacy-valid` let through.
 */
export function memoryFrontmatter(): LintRule {
  return {
    id: "memory-frontmatter",
    description:
      "Ensure data/_memory entries declare a top-level memory `type` (not under `metadata`, not the generic `note`)",
    check({ file, content }) {
      const findings: LintFinding[] = [];
      const norm = file.replace(/\\/g, "/");
      if (!/(?:^|\/)_memory\//.test(norm)) return findings;
      const base = norm.slice(norm.lastIndexOf("/") + 1);
      if (base === "_INDEX.md" || base === "MEMORY.md" || base.startsWith("_TEMPLATE")) {
        return findings;
      }

      const { frontmatter } = parseFrontmatter<Record<string, unknown>>(content);

      const metadata = frontmatter["metadata"];
      if (
        metadata !== null &&
        typeof metadata === "object" &&
        !Array.isArray(metadata) &&
        "type" in (metadata as Record<string, unknown>)
      ) {
        findings.push({
          rule: "memory-frontmatter",
          severity: "error",
          file,
          message:
            "Memory `type` must be a top-level frontmatter field, not nested under `metadata` — the index and recall layers read top-level `type`.",
        });
      }

      if (frontmatter["type"] === "note") {
        findings.push({
          rule: "memory-frontmatter",
          severity: "error",
          file,
          message:
            "Memory `type: note` misclassifies the entry; use a memory type (e.g. feedback/user/project/reference, or an instance extension).",
        });
      }

      return findings;
    },
  };
}
